import { StatsData, useGetStats } from "hooks/LabelPropagation/useGetStats";
import { useFlywheelCycle } from "./useFlywheelCycle";
import {
  ClassOption,
  StepConfig,
} from "views/components/LabelPropagation/types";
import SeedStep, {
  SeedStepRef,
} from "views/components/LabelPropagation/SeedStep";
import { CardItem } from "views/components/LabelPropagation/SelectedClassesListStep/ClassCard";
import AddClassButton from "views/components/LabelPropagation/SeedStep/AddClassButton";
import ProgressStep, {
  LoadingType,
} from "views/components/LabelPropagation/ProgressStep";
import {
  VLButtonProps,
  VLPrimaryButtonProps,
} from "views/uikit/Buttons/VLButton";
import { useCallback, useEffect, useState } from "react";
import { App } from "antd";
import { useLocation, useNavigate } from "react-router";
import {
  FLOW_PARAMETER,
  LABEL_CLASS_PARAMETER,
  MANUAL_REVIEW_FLOW,
  REVIEW_PARAMETER,
  REVIEW_TAB_PARAMETER,
  SEED_PARAMETER,
} from "../../helpers/constants/miscellaneous";
import {
  FlowStatus,
  usePollSeedStatusAndStats,
} from "./usePollSeedStatusAndStats";
import { useStartSeed } from "./useStartSeed";
import { useGetSeed } from "./useGetSeed";
import { FlywheelStatus } from "../../vl-api/vlSchemas";
import { useRefetchFlywheelStatus } from "./useFlywheelStatus";
import { useSearchParams } from "react-router-dom";
import { useClearLabelPropagationSearchParams } from "./useClearLabelPropagationSearchParams";

interface UseStepsConfigProps {
  selectedValues?: string[];
  setSelectedValues: React.Dispatch<React.SetStateAction<string[] | undefined>>;
  options: ClassOption[];
  selectedCard: string | null;
  progressLimit: number;
  loading?: boolean;
  error?: any;
  allClassesMeetLimit: boolean;
  onDelete?: (value: string) => void;
  onItemDelete?: (value: string, mediaId: string) => void;
  classItemsMap?: Record<string, CardItem[]>;
  seedId?: string | null;
  step: number;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  datasetId: string;
  requiredSeedSamplesPerLabel: number;
  seedStepRef?: React.RefObject<SeedStepRef>;
  setClassItemsMap: React.Dispatch<
    React.SetStateAction<Record<string, CardItem[]>>
  >;
  shouldFetch?: string;
  resetShouldFetch: () => void;
  onLabelPropagationCompleted: () => void;
  flywheelStatus?: FlywheelStatus;
  showSummaryTab: () => void;
}

export const useStepsConfig = ({
  selectedValues,
  setSelectedValues,
  options,
  selectedCard,
  progressLimit,
  loading,
  error,
  allClassesMeetLimit,
  onDelete,
  onItemDelete,
  classItemsMap,
  setStep,
  datasetId,
  requiredSeedSamplesPerLabel,
  seedStepRef,
  setClassItemsMap,
  shouldFetch,
  resetShouldFetch,
  onLabelPropagationCompleted,
  flywheelStatus,
  showSummaryTab,
}: UseStepsConfigProps): StepConfig[] => {
  const { modal } = App.useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const seedId = searchParams.get(SEED_PARAMETER);
  const isReview = searchParams.get(REVIEW_PARAMETER);
  const [active, setActive] = useState<boolean>(false);

  useEffect(() => {
    switch (flywheelStatus) {
      case "RESUME_FLOW":
        setActive(true);
        setLoadingType("SUBMIT");
        break;
      case "INIT":
        setActive(true);
        setLoadingType("RUN");
        break;
      case "APPLY_MODEL_TO_DATASET":
        setActive(true);
        setLoadingType("FINISH");
        break;
      default:
        break;
    }
  }, [flywheelStatus, setActive]);

  const [startSeed, setStartSeed] = useState(false);

  const [doneAction, setDoneAction] = useState<() => void>();

  const [loadingType, setLoadingType] = useState<LoadingType>();

  const [flowId, setFlowId] = useState(searchParams.get(FLOW_PARAMETER));

  const { fetchSeed, seed } = useGetSeed();

  const updateFlywheelStatus = useRefetchFlywheelStatus();

  const { status, isFinalStatus } = usePollSeedStatusAndStats({
    datasetId,
    flowId: flowId ?? null,
    seedId,
    active,
  });

  const { flowId: startedFlowId } = useStartSeed({
    datasetId: datasetId || "",
    seedId: seedId || "",
    active: startSeed && !flowId,
  });

  useEffect(() => {
    if (startedFlowId && !flowId) {
      setFlowId(startedFlowId);
      setActive(true);
      setStartSeed(false);
      if (!searchParams.has(FLOW_PARAMETER)) {
        searchParams.set(FLOW_PARAMETER, startedFlowId);
        setSearchParams(searchParams);
      }
    }
  }, [searchParams, setSearchParams, startedFlowId, flowId]);

  const clearLabelPropagationSearchParams =
    useClearLabelPropagationSearchParams();

  const onAborted = useCallback(() => {
    clearLabelPropagationSearchParams();
    showSummaryTab();
  }, [clearLabelPropagationSearchParams, showSummaryTab]);

  const onFinished = () => {
    setLoadingType("FINISH");
    setActive(true);
  };

  const { runCycle, finishCycle, resetCycle } = useFlywheelCycle({
    onAborted,
    onFinished,
  });

  const { fetchStats, stats } = useGetStats();

  const displayReviewStepNextBtn =
    stats?.review.mainProgress &&
    stats.review.mainProgress.maxItems === stats.review.mainProgress.itemLength;

  useEffect(() => {
    seedId && fetchSeed(datasetId, seedId);
    flowId && fetchStats(datasetId, flowId);
  }, [seedId, datasetId, fetchSeed, flowId, fetchStats]);

  useEffect(() => {
    if (!active || !status) return;
    if (status === FlowStatus.PAUSED_FOR_REVIEW) {
      (async () => {
        try {
          if (flowId) await fetchStats(datasetId, flowId);
          const action = doneAction;
          if (action) action();
        } finally {
          setLoadingType(undefined);
          setActive(false);
          updateFlywheelStatus("PAUSED_FOR_REVIEW");
        }
      })();
    }
  }, [
    isFinalStatus,
    active,
    datasetId,
    flowId,
    fetchStats,
    doneAction,
    status,
    updateFlywheelStatus,
  ]);

  useEffect(() => {
    if (!active || !status) return;

    if (status === FlowStatus.COMPLETED && flowId) {
      setActive(false);
      fetchStats(datasetId, flowId).then((res) => {
        setLoadingType(undefined);
        clearLabelPropagationSearchParams();
        const statsData = res as StatsData;
        const totalItemsText =
          statsData.stats.total_annotated === statsData.total_media_count
            ? "All"
            : statsData.stats.total_annotated;
        modal.success({
          title: "Label Propagation Completed!",
          content: `${totalItemsText} Images were successfully labeled in your dataset.`,
          footer: false,
          closable: true,
          width: 520,
          autoFocusButton: null,
        });
        onLabelPropagationCompleted();
      });
    }
  }, [
    active,
    clearLabelPropagationSearchParams,
    datasetId,
    fetchStats,
    flowId,
    modal,
    onLabelPropagationCompleted,
    status,
  ]);

  const onFinishClicked = useCallback(() => {
    modal.info({
      title: "Ready to submit your feedback?",
      content:
        "Once you submit, label propagation will stop and all labels annotated up to this point will be saved as final.",
      okText: "Yes, Finalize",
      width: 520,
      closable: true,
      onOk: () => {
        if (datasetId && flowId) {
          finishCycle(datasetId, flowId);
        }
      },
    });
  }, [modal, datasetId, flowId, finishCycle]);

  const onAbortClicked = useCallback(() => {
    modal.error({
      title: "All progress will be lost. Are you sure?",
      content:
        "Resetting removes all annotations from label propagation. Any new labels or progress made will not be saved. You’ll return to the beginning.",
      okText: "Reset",
      okButtonProps: { danger: true },
      width: 560,
      closable: true,
      onOk: () => {
        if (datasetId && flowId) {
          resetCycle(datasetId, flowId);
        }
      },
    });
  }, [modal, datasetId, flowId, resetCycle]);

  const getProgressStepNextBtn =
    useCallback((): VLPrimaryButtonProps | null => {
      if (isReview) {
        return {
          text: "Submit",
          type: "primary",
          isGradient: true,
          disabled: !displayReviewStepNextBtn || !!loadingType,
          tooltip: loadingType ? "Submitting..." : undefined,
          loading: !!loadingType,
          onClick: async () => {
            setLoadingType("SUBMIT");
            if (!flowId || !datasetId) return;

            setDoneAction(() => {});

            await runCycle(datasetId, flowId);
            fetchStats(datasetId, flowId);
            setActive(true);
            setLoadingType("SUBMIT");
            const params = new URLSearchParams(location.search);
            params.delete(LABEL_CLASS_PARAMETER);
            params.delete(REVIEW_PARAMETER);
            params.delete(REVIEW_TAB_PARAMETER);
            params.delete(MANUAL_REVIEW_FLOW);
            navigate({
              pathname: location.pathname,
              search: params.toString(),
            });
          },
        };
      }
      return {
        text: "Finish",
        type: "primary",
        onClick: onFinishClicked,
        disabled: !!loadingType,
      };
    }, [
      isReview,
      onFinishClicked,
      displayReviewStepNextBtn,
      loadingType,
      flowId,
      datasetId,
      runCycle,
      fetchStats,
      location.search,
      location.pathname,
      navigate,
    ]);

  const getProgressStepPrevBtn = useCallback((): VLButtonProps | null => {
    if (!isReview)
      return {
        text: "Reset",
        type: "default",
        onClick: onAbortClicked,
        disabled: !!loadingType,
      };
    return null;
  }, [onAbortClicked, isReview, loadingType]);

  const getSeedStepNextBtn = useCallback(
    (): VLPrimaryButtonProps | null => ({
      text: "Start Label Propagation",
      type: "primary",
      disabled:
        !allClassesMeetLimit || (selectedValues && selectedValues?.length < 2),
      onClick: () => {
        setLoadingType("RUN");
        setStep(2);
        setStartSeed(true);
      },
      tooltip:
        selectedValues && selectedValues?.length < 2
          ? "Select at least two labels to continue"
          : !allClassesMeetLimit
            ? `Select at least ${requiredSeedSamplesPerLabel} items per label to continue.`
            : undefined,
    }),
    [allClassesMeetLimit, selectedValues, requiredSeedSamplesPerLabel, setStep],
  );

  useEffect(() => {
    if (!flowId || !datasetId) return;
    shouldFetch && shouldFetch.length > 0 && fetchStats(datasetId, flowId);
    resetShouldFetch();
  }, [datasetId, fetchStats, flowId, shouldFetch, resetShouldFetch]);

  // Title/subtitle config based on whether any classes are selected
  const titleByMode: Record<
    "selected" | "default",
    { text: string; subText?: string }
  > = {
    selected: {
      text: "Add Samples",
    },
    default: {
      text: "Label Propagation",
      subText: "Propagate Labels Across Your Dataset",
    },
  };

  return [
    {
      key: 1,
      title: selectedValues
        ? {
            ...titleByMode[selectedValues.length > 0 ? "selected" : "default"],
            icon:
              selectedValues.length > 0 ? (
                <AddClassButton
                  onClick={() => seedStepRef?.current?.handleOpenModal()}
                />
              ) : undefined,
            count:
              classItemsMap && Object.keys(classItemsMap).length > 0
                ? Object.values(classItemsMap).reduce(
                    (total, items) => total + items.length,
                    0,
                  )
                : undefined,
          }
        : undefined,
      content: (
        <SeedStep
          ref={seedStepRef}
          selectedValues={selectedValues}
          setSelectedValues={setSelectedValues}
          options={options}
          loading={loading}
          error={error}
          progressLimit={progressLimit}
          selectedCard={selectedCard}
          onDelete={onDelete ?? (() => {})}
          onItemDelete={onItemDelete ?? (() => {})}
          classItemsMap={classItemsMap}
          setClassItemsMap={setClassItemsMap}
          seedId={seedId}
          datasetId={datasetId}
          displayAddClassButton={selectedValues?.length === 0}
        />
      ),
      nextButton: selectedValues?.length ? getSeedStepNextBtn() : null,
      prevButton: null,
    },
    {
      key: 2,
      content: (
        <ProgressStep
          seedId={seedId}
          initialFlowId={flowId}
          stats={stats}
          loadingType={loadingType}
          seed={seed}
        />
      ),
      nextButton: getProgressStepNextBtn(),
      prevButton: getProgressStepPrevBtn(),
    },
  ];
};
